import React from 'react';
import { objectKeysToCamelCase } from '@heidi-pay/heidi-common-fe/utils/objectKeysToCamelCase';
import { QueryKey } from '@tanstack/react-query';
import { ActionButtons } from '@appTypes';
import { Card } from '@components/Card';
import { useI18n } from '@hooks/useI18n';
import { IMerchantPortalOrderApplicationDetails } from '@schemas/merchantPortal/types/orderApplicationDetails';
import { ApplicationDocumentsStatusCards } from './ApplicationDocumentsStatusCards/ApplicationDocumentsStatusCards';
import { ApplicationDocumentsTable } from './ApplicationDocumentsTable/ApplicationDocumentsTable';
import { useGetApplicationDocumentButtons } from './hooks';

interface IApplicationDocumentUploads {
  externalContractUuid: string | undefined;
  orderApplicationDetailsData: IMerchantPortalOrderApplicationDetails;
  queryKey: QueryKey;
  wrapInCard?: boolean;
}

interface IWrapper {
  buttons: ActionButtons;
  children: React.ReactNode;
  title: string;
  wrapInCard: boolean;
}

const Wrapper = ({ buttons, children, title, wrapInCard }: IWrapper) =>
  wrapInCard ? (
    <Card buttons={buttons} title={title}>
      {children}
    </Card>
  ) : (
    <>{children}</>
  );

export const ApplicationDocumentUploads = ({
  externalContractUuid,
  orderApplicationDetailsData,
  queryKey,
  wrapInCard = true,
}: IApplicationDocumentUploads) => {
  const { translate } = useI18n();

  const { customerAgreementSigningOverview, manualIdentityUploadSummary } =
    objectKeysToCamelCase(orderApplicationDetailsData);

  const buttons = useGetApplicationDocumentButtons({
    customerAgreementSigningOverview,
    externalContractUuid,
    manualIdentityUploadSummary,
    queryKey,
  });

  return (
    <Wrapper
      buttons={buttons}
      title={translate('TITLE.CUSTOMER_DOCUMENTS')}
      wrapInCard={wrapInCard}
    >
      <ApplicationDocumentsStatusCards
        customerAgreementSigningOverview={customerAgreementSigningOverview}
        manualIdentityUploadSummary={manualIdentityUploadSummary}
      />

      <ApplicationDocumentsTable
        documents={orderApplicationDetailsData.documents}
      />
    </Wrapper>
  );
};
